import React, { useEffect, useState, useMemo } from "react";
import clsx from "clsx";
import { Form } from "react-bootstrap";
import { useFormContext } from "react-hook-form";

import {
	FormControlLabel,
	GenericSwitch,
	HookFormInputGroup,
	HookFormRadioButton,
	HookSelectFormGroup,
	RequiredMark,
	Skeleton,
	TextWithLoader,
} from "components";
import { FormRow, FormTitle, FormColumn } from "components/layout";
import { DebounceTimeout } from "globalConstants";
import { useLoader } from "hooks";
import { StatusValues } from "mappers";
import { ResidualsSplitProfileType, ScenarioTypes } from "types";
import { trimNormalizer } from "validators";
import { maxLength150, minLength2, required } from "validators/validation";

import { TotalRevenueShareField } from "./totalRevenueShareField";

import styles from "./../splitProfile.module.scss";

type GeneralFieldsProps = {
	isLoading: boolean;
	isAgentDataLoading: boolean;
	scenarioType: { [key: string]: any };
	splitProfile: ResidualsSplitProfileType;
	isEditing: boolean;
	toggleAgentSelect: boolean;
};

export const GeneralFields = ({
	isLoading,
	isAgentDataLoading,
	scenarioType,
	splitProfile,
	isEditing,
	toggleAgentSelect,
}: GeneralFieldsProps) => {
	const [initialAutogenerateProfileName, setInitialAutogenerateProfileName] =
		useState("");
	const [autogenerateFileName, setAutogenerateFileName] = useState("");
	const [isManualEditedProfileName, setIsManualEditedProfileName] =
		useState(false);
	const [focusProfileName, setFocusProfileName] = useState(false);

	const Loader = useLoader(isLoading);

	const {
		register,
		watch,
		formState: { errors },
		setValue,
	} = useFormContext();

	const profileName = watch("profileName");
	const totalRevenueShare = watch("totalRevenueShare");
	const primarySplitInformation = watch("primarySplitInformation");
	const secondarySplitInformation = watch("secondarySplitInformation");
	const status = watch("status");
	const highRisk = watch("isHighRisk");

	const isAvailable = status?.value;
	const isHighRisk = highRisk === "true";

	// Scenarios
	const isStraightSplit = useMemo(
		() => scenarioType?.value === ScenarioTypes?.StraightSplit.value,
		[scenarioType?.value],
	);
	const isWaterfall = useMemo(
		() => scenarioType?.value === ScenarioTypes?.Waterfall.value,
		[scenarioType?.value],
	);

	// setInitialAutogenerateProfileName
	useEffect(() => {
		if (!splitProfile?.primarySplitInformation.agent) return;

		const profileSplitPercents =
			splitProfile?.scenarioType?.value ===
			ScenarioTypes?.StraightSplit.value
				? splitProfile?.totalRevenueShare || ""
				: splitProfile?.primarySplitInformation?.splitPercent || "";

		const primaryAgentFirstName =
			splitProfile?.primarySplitInformation.agent.firstName.slice(0, 25);

		let secondarySplitAgents = "";

		splitProfile?.secondarySplitInformation?.forEach(item => {
			if (!item.agent) return;

			const secondaryAgentFirstName = item.agent.firstName.slice(0, 25);

			secondarySplitAgents += secondarySplitAgents
				? `, ${secondaryAgentFirstName}`
				: `${secondaryAgentFirstName}`;
		});

		const initialProfileName =
			`${splitProfile?.scenarioType?.name}: ${primaryAgentFirstName}` +
			(profileSplitPercents ? ` ${profileSplitPercents} %` : "") +
			(secondarySplitAgents ? `: Overrides ${secondarySplitAgents}` : "");

		setInitialAutogenerateProfileName(initialProfileName);
	}, [
		splitProfile?.primarySplitInformation.agent,
		splitProfile?.primarySplitInformation?.splitPercent,
		splitProfile?.scenarioType?.name,
		splitProfile?.scenarioType?.value,
		splitProfile?.secondarySplitInformation,
		splitProfile?.totalRevenueShare,
	]);

	// setAutogenerateFileName
	useEffect(() => {
		if (!primarySplitInformation.agent) return;

		const profileSplitPercents = isStraightSplit
			? totalRevenueShare
			: primarySplitInformation.splitPercent;

		const primaryAgentFirstName =
			primarySplitInformation?.agent?.firstName?.slice?.(0, 25);

		let secondarySplitAgents = "";

		secondarySplitInformation?.forEach(item => {
			if (!item.agent) return;

			const secondaryAgentFirstName = item.agent.firstName.slice(0, 25);
			secondarySplitAgents += secondarySplitAgents
				? `, ${secondaryAgentFirstName}`
				: `${secondaryAgentFirstName}`;
		});

		const profileNameValue =
			`${scenarioType?.displayName}: ${primaryAgentFirstName}` +
			(profileSplitPercents ? ` ${profileSplitPercents} %` : "") +
			(secondarySplitAgents ? `: Overrides ${secondarySplitAgents}` : "");

		setAutogenerateFileName(profileNameValue);
	}, [
		toggleAgentSelect,
		primarySplitInformation.agent,
		primarySplitInformation.splitPercent,
		scenarioType?.displayName,
		scenarioType.value,
		secondarySplitInformation,
		totalRevenueShare,
		isStraightSplit,
	]);

	// setIsManualEditedProfileName
	useEffect(() => {
		if (splitProfile?.profileName) {
			if (initialAutogenerateProfileName !== splitProfile?.profileName) {
				setIsManualEditedProfileName(true);
			} else {
				setIsManualEditedProfileName(false);
			}
		}
	}, [splitProfile?.profileName, initialAutogenerateProfileName, isLoading]);

	useEffect(() => {
		if (!isManualEditedProfileName) {
			setValue("profileName", autogenerateFileName);
		}
	}, [isManualEditedProfileName, autogenerateFileName, setValue]);

	// setIsManualEditedProfileName
	useEffect(() => {
		if (splitProfile?.profileName) {
			if (
				focusProfileName &&
				initialAutogenerateProfileName !== profileName
			) {
				setIsManualEditedProfileName(true);
			}
		} else {
			if (focusProfileName && autogenerateFileName !== profileName) {
				setIsManualEditedProfileName(true);
			}
		}
	}, [
		autogenerateFileName,
		focusProfileName,
		initialAutogenerateProfileName,
		profileName,
		splitProfile?.profileName,
	]);

	// Reset isDefault if Unavailable or isHighRisk
	useEffect(() => {
		if (!isAvailable || isHighRisk) {
			setValue("isDefault", false);
		}
	}, [isHighRisk, isAvailable, setValue]);

	const handleFocusProfileName = focus => {
		setFocusProfileName(focus);
	};

	return (
		<>
			<FormTitle>
				<h3>Residuals Split Profile Information</h3>
			</FormTitle>
			{isAvailable && !isHighRisk && (
				<FormRow className="my-4">
					<FormColumn data-autotest="profileIsDefault">
						<Loader
							skeleton={<Skeleton width="100%" height={34} />}
							component={
								<FormControlLabel
									control={
										<GenericSwitch
											register={register}
											name="isDefault"
											id="isDefault"
										/>
									}
									label="Default Split Profile"
								/>
							}
						/>
					</FormColumn>
				</FormRow>
			)}
			<FormRow>
				<FormColumn data-autotest="profileId">
					{(splitProfile || isEditing) && (
						<TextWithLoader
							label="Profile ID:"
							value={splitProfile?.userFriendlyId}
							loading={isLoading}
							labelCol={6}
							valueCol={6}
						/>
					)}
				</FormColumn>
				<FormColumn data-autotest="status">
					<HookSelectFormGroup
						label="Status"
						options={StatusValues}
						getOptionLabel={option => option.name}
						getOptionValue={option => option.value}
						placeholder="Please Select"
						name="status"
						items={StatusValues}
						cacheOptions
						defaultOptions
						debounceTimeout={DebounceTimeout}
						rules={{
							validate: {
								required,
							},
						}}
						showRequiredMark
						blurLoading={isLoading}
					/>
				</FormColumn>
			</FormRow>
			<FormRow>
				<FormColumn data-autotest="profileName">
					<HookFormInputGroup
						register={register}
						name="profileName"
						label="Profile Name"
						placeholder="Enter Profile Name"
						colLabel={6}
						colInput={6}
						validator={{
							required,
							minLength2,
							maxLength150,
						}}
						normalizeOnBlur={trimNormalizer}
						onFocus={handleFocusProfileName}
						blurLoading={isLoading || isAgentDataLoading}
						errors={errors}
						showRequiredMark
					/>
				</FormColumn>
				<FormColumn data-autotest="isHighRisk">
					<div className="form-group row">
						<Form.Label
							htmlFor="isHighRisk"
							className="form-label col-form-label col-sm-6"
						>
							Is Profile High Risk?
							<RequiredMark />
						</Form.Label>
						<div className="col-sm-6">
							<div className={clsx(styles.wrappRadio)}>
								<HookFormRadioButton
									register={register}
									label="Yes"
									value="true"
									name="isHighRisk"
									className={styles.radioButton}
									id="isHighRisk"
									blurLoading={isLoading}
								/>
								<HookFormRadioButton
									register={register}
									label="No"
									value="false"
									name="isHighRisk"
									className={styles.radioButton}
									id="isNotHighRisk"
									blurLoading={isLoading}
								/>
							</div>
						</div>
					</div>
				</FormColumn>
			</FormRow>
			<FormRow>
				<FormColumn data-autotest="scenarioType">
					<HookSelectFormGroup
						label="Scenario"
						options={ScenarioTypes.getOptions()}
						getOptionLabel={option => option.displayName}
						getOptionValue={option => option.value}
						placeholder="Please Select"
						name="scenarioType"
						cacheOptions
						defaultOptions
						debounceTimeout={DebounceTimeout}
						rules={{
							validate: {
								required,
							},
						}}
						showRequiredMark
						blurLoading={isLoading}
					/>
				</FormColumn>
				<FormColumn data-autotest="totalRevenueShare">
					{
						<TotalRevenueShareField
							isLoading={isLoading}
							isWaterfall={isWaterfall}
						/>
					}
				</FormColumn>
			</FormRow>
		</>
	);
};
